import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { SignIn, SignUp } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { ThemeProvider, useTheme } from "@/lib/theme";
import { MeProvider } from "@/lib/me";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { RequireAuth, EmptyState } from "@/components/shared/common";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import { ServiceDetail, ProductDetail } from "@/pages/Details";
import { VendorStore, ProjectDetail } from "@/pages/Store";
import Legal from "@/pages/Legal";
import { Categories, Pricing, About, Contact, Help, Blog, BlogPost, PaymentSuccess, PaymentCancel } from "@/pages/Static";
import OrderDetail from "@/pages/OrderDetail";
import { Messages, Notifications } from "@/pages/Inbox";
import DashboardLayout from "@/pages/dashboard/Layout";
import { BuyerOverview, BuyerOrders, Purchases, MyProjects, Favorites, Payments, Invoice, Refunds, Support, ProfileSettings, PostProject } from "@/pages/dashboard/Buyer";
import { VendorOverview, StorefrontEditor, VendorServices, VendorProducts, ServiceEditor, ProductEditor, VendorOrders, VendorProposals, VendorCustomers, VendorCoupons, VendorPayouts, VendorReviews, VendorTeam, VendorGuard } from "@/pages/dashboard/Vendor";
import Admin from "@/pages/dashboard/Admin";
import DeveloperControl from "@/pages/DeveloperControl";
import { PayPage, PayThanks } from "@/pages/PaymentLinks";

const ScrollTop = () => { const { pathname } = useLocation(); useEffect(() => { window.scrollTo(0, 0); }, [pathname]); return null; };

const AuthPage = ({ children }) => (
  <div className="container-x grid min-h-[75vh] gap-10 py-12 lg:grid-cols-2">
    <div className="hidden flex-col justify-center lg:flex">
      <p className="eyebrow mb-4">DevMarket</p>
      <h1 className="text-4xl font-extrabold tracking-tight">One account to buy, hire and sell.</h1>
      <p className="mt-4 max-w-md text-muted-foreground">Every account can buy services, post projects and open a seller storefront. Sign in with email or Google.</p>
    </div>
    <div className="flex items-center justify-center">{children}</div>
  </div>
);

const P = (el) => <RequireAuth>{el}</RequireAuth>;
const V = (el) => <VendorGuard>{el}</VendorGuard>;

function Shell() {
  const { theme } = useTheme();
  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Browse key="services" kind="services" />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/products" element={<Browse key="products" kind="products" />} />
          <Route path="/products/:slug" element={<ProductDetail />} />
          <Route path="/developers" element={<Browse key="developers" kind="developers" />} />
          <Route path="/agencies" element={<Browse key="agencies" kind="agencies" />} />
          <Route path="/projects" element={<Browse key="projects" kind="projects" />} />
          <Route path="/projects/new" element={P(<PostProject />)} />
          <Route path="/projects/:id" element={<ProjectDetail />} />
          <Route path="/store/:slug" element={<VendorStore />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/help" element={<Help />} />
          <Route path="/faq" element={<Help />} />
          <Route path="/legal/:page" element={<Legal />} />
          <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
          <Route path="/privacy-policy" element={<Navigate to="/legal/privacy" replace />} />
          <Route path="/refund-policy" element={<Navigate to="/legal/refunds" replace />} />
          <Route path="/shipping-policy" element={<Navigate to="/legal/shipping" replace />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/sign-in/*" element={<AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthPage>} />
          <Route path="/sign-up/*" element={<AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>} />
          <Route path="/payment/success" element={P(<PaymentSuccess />)} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/orders/:id" element={P(<OrderDetail />)} />
          <Route path="/invoice/:id" element={P(<Invoice />)} />
          <Route path="/messages" element={P(<Messages />)} />
          <Route path="/messages/:id" element={P(<Messages />)} />
          <Route path="/notifications" element={P(<Notifications />)} />
          {process.env.REACT_APP_DEVCONSOLE_PATH && <Route path={process.env.REACT_APP_DEVCONSOLE_PATH} element={<DeveloperControl />} />}
          <Route path="/pay/:id" element={<PayPage />} />
          <Route path="/pay/:id/thanks" element={<PayThanks />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/:section" element={<Admin />} />
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<BuyerOverview />} />
            <Route path="orders" element={<BuyerOrders />} />
            <Route path="purchases" element={<Purchases />} />
            <Route path="projects" element={<MyProjects />} />
            <Route path="favorites" element={<Favorites />} />
            <Route path="payments" element={<Payments />} />
            <Route path="refunds" element={<Refunds />} />
            <Route path="support" element={<Support />} />
            <Route path="settings" element={<ProfileSettings />} />
            <Route path="vendor/storefront" element={<StorefrontEditor />} />
            <Route path="vendor" element={V(<VendorOverview />)} />
            <Route path="vendor/services" element={V(<VendorServices />)} />
            <Route path="vendor/services/:id" element={V(<ServiceEditor />)} />
            <Route path="vendor/products" element={V(<VendorProducts />)} />
            <Route path="vendor/products/:id" element={V(<ProductEditor />)} />
            <Route path="vendor/orders" element={V(<VendorOrders />)} />
            <Route path="vendor/proposals" element={V(<VendorProposals />)} />
            <Route path="vendor/customers" element={V(<VendorCustomers />)} />
            <Route path="vendor/coupons" element={V(<VendorCoupons />)} />
            <Route path="vendor/payouts" element={V(<VendorPayouts />)} />
            <Route path="vendor/reviews" element={V(<VendorReviews />)} />
            <Route path="vendor/team" element={V(<VendorTeam />)} />
          </Route>
          <Route path="*" element={<div className="container-x py-24"><EmptyState title="Page not found" text="The page you're looking for doesn't exist." /></div>} />
        </Routes>
      </main>
      <Footer />
      <Toaster richColors position="top-right" theme={theme} />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ClerkAuthProvider>
          <MeProvider>
            <ScrollTop />
            <Shell />
          </MeProvider>
        </ClerkAuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
